import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { Breadcrumb } from 'ui/breadcrumb';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { FormField } from 'ui/formField';
import { Refresh } from 'ui/icons/action/Refresh';
import { InputText } from 'ui/inputs';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { body2, colors, duration, easing, spacing } from 'ui/theme';

import { usePanelContext } from '../../contexts/Panel';
import { useTranslations } from '../../hooks/useTranslations';
import { Asset, ImageAttrBatch, ImageAttrs, ImageAttrState, PanelView } from '../../types';
import { FileDetails } from '../index/details/FileDetails';
import { InputImageURL } from '../index/details/InputImageURL';
import { AltTextInput } from '../common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { HtmlEditor } from 'modules/graphql/types';

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${spacing.half};
`;

const DetailViewBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  row-gap: ${spacing.base};
`;

const Label = styled.label`
  ${body2};
  cursor: pointer;
  margin: 0;
`;

const DetailsDimensions = styled.div`
  align-items: flex-end;
  display: flex;
  column-gap: ${spacing.half};

  > * {
    flex: 1 1 auto;
  }

  input {
    width: 100%;
  }
`;

const DetailsProportions = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const FileArea = css`
  flex: 0 0 auto;
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 65%;
`;

const FilePreview = styled.div<{ $imgSrc: string }>`
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundTertiary};
  background-image: url(${({ $imgSrc }) => $imgSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-origin: content-box;
  padding: ${spacing.double};
  background-size: contain;
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.threeEights};
  ${FileArea}
`;

const FilePreviewReplace = styled.div`
  align-items: center;
  bottom: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  transition: all ${duration.short} ${easing.entrance};

  button {
    visibility: hidden;
  }

  :hover {
    background-color: rgba(24, 4, 28, 0.33);

    button {
      visibility: visible;
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.double};
  padding-bottom: ${spacing.base};
  flex: 1;
`;

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

export const testIds = {
  breadcrumb: 'cl-panel-detail-breadcrumb',
  btnResetDimensions: 'cl-panel-detail-btn-reset',
  btnSelect: 'cl-panel-detail-btn-select',
  checkboxAR: 'cl-panel-detail-checkbox-ar',
  detailsNode: 'cl-panel-detail-file-details-node',
  detailsSelected: 'cl-panel-detail-file-details-selected',
  dropzone: 'cl-panel-detail-dropzone',
  htmlAttrs: 'cl-panel-detail-html-attrs',
  inputAltText: 'cl-panel-detail-input-alt-text',
  inputHeight: 'cl-panel-detail-input-height',
  inputStyles: 'cl-panel-detail-input-styles',
  inputWidth: 'cl-panel-detail-input-width',
  preview: 'cl-panel-detail-preview',
  previewReplace: 'cl-panel-detail-preview-replace',
};

export const PanelViewDetail = () => {
  const { t } = useTranslations();
  const {
    assetUrl,
    imageAttrState,
    onInsert,
    onSelect,
    providerHasInitiated,
    selectedAsset,
    selectedEditorImageNode,
    selectedRemoteImageNode,
    setImageAttrState,
    setNonCustomizableAttrs,
    setPanelView,
  } = usePanelContext();
  const { contentEditor } = useEditableFieldsContext();
  const isBeeEditor = contentEditor === HtmlEditor.Bee;

  const [lockAspectRatio, setLockAspectRatio] = useState<boolean>(true);
  const [naturalAR, setNaturalAR] = useState<number>(1);

  const hasRequiredFileProps = (obj: any) =>
    Boolean(obj && obj.size && obj.height && obj.width && obj.mimeType);

  const hasFileDetails = hasRequiredFileProps(selectedAsset);

  // determine the current image's aspect ratio & update the attribute state with the current image selection
  const setImageState = (remoteImgNode: HTMLImageElement, asset?: Asset) => {
    // Priority:
    // 1 - remoteImgNode: an image node created by entering or editing a remote url (could be any url, even one pointing to an Asset Manager asset)
    // 2 - asset: an asset selected from the Asset Manager (Iterable hosts and has detailed information)
    // 3 - selectedEditorImageNode: an image selected in the WYSIWYG editor when opening the panel (we can read all of the existing attributes on the node)
    // calculate the natural aspect ratio
    const naturalAspectRatio =
      (remoteImgNode && remoteImgNode.naturalWidth / remoteImgNode.naturalHeight) ||
      (asset && asset.width / asset.height) ||
      (selectedEditorImageNode &&
        selectedEditorImageNode.naturalWidth / selectedEditorImageNode.naturalHeight) ||
      1;
    setNaturalAR(naturalAspectRatio);
    // Update Image State with Selected Image Details
    const attrValues = { ...imageAttrState } as ImageAttrState;
    const hasBothWidthAndHeight =
      imageAttrState[ImageAttrs.WIDTH] && imageAttrState[ImageAttrs.HEIGHT];
    if (remoteImgNode) {
      // only one of remoteImgNode or asset should exist at a time (enforced in CreativeLibraryProvider)
      attrValues[ImageAttrs.SOURCE] = remoteImgNode.src.trim();
      if (hasBothWidthAndHeight && lockAspectRatio) {
        attrValues[ImageAttrs.HEIGHT] = Math.round(
          parseInt(imageAttrState[ImageAttrs.WIDTH], 10) / naturalAspectRatio,
        ).toString();
      }
      // otherwise:
      // - has neither width nor height attr: do nothing because if only a single style height or
      //   width is set, the image may get distorted
      // - has only one of width or height attr: do nothing (the missing attribute defaults to auto)
    } else if (asset) {
      attrValues[ImageAttrs.SOURCE] = (asset.url || '').trim();
      attrValues[ImageAttrs.ALT_DESC] = (asset.altText || '').trim();
      attrValues[ImageAttrs.WIDTH] = asset.width?.toString();
      attrValues[ImageAttrs.HEIGHT] = asset.height?.toString();
    }
    // set the attributes
    setImageAttrState({ type: ImageAttrBatch.UPDATE_BATCH, value: attrValues });
  };

  const handleNonCustomizableAttrs = (image: HTMLImageElement) => {
    const customizableAttrs = [
      ImageAttrs.ALT_DESC,
      ImageAttrs.HEIGHT,
      ImageAttrs.SOURCE,
      ImageAttrs.STYLES,
      ImageAttrs.WIDTH,
    ] as string[];
    const otherAttrs: Record<string, string> = {};

    // the data-mce attributes are not applicable to the DOMUtils used to set the new attributes
    image.getAttributeNames().forEach((attrName) => {
      if (customizableAttrs.indexOf(attrName) === -1 && !attrName.startsWith('data-mce')) {
        otherAttrs[attrName] = image.getAttribute(attrName) || '';
      }
    });
    setNonCustomizableAttrs(otherAttrs);
  };

  const handleResponsiveSizing = (value: string, dimension: ImageAttrs) => {
    const altDim = dimension === ImageAttrs.HEIGHT ? ImageAttrs.WIDTH : ImageAttrs.HEIGHT;
    const size = value.replace(/[^\d]+/g, '');
    const sizeInt = parseInt(size, 10);
    setImageAttrState({ type: dimension, value: size });
    // only update the alternate dimension if it was previously defined
    if (lockAspectRatio && sizeInt && imageAttrState[altDim]) {
      setImageAttrState({
        type: altDim,
        value: Math.round(
          altDim === ImageAttrs.HEIGHT ? sizeInt / naturalAR : sizeInt * naturalAR,
        ).toString(),
      });
    }
  };

  // set the image state on any change to the selected remote image or asset
  // this responds to edits of the remote url and direct uploads to the empty state
  // selectedEditorImageNode will not change in the lifecycle of the panel
  useEffect(() => {
    if (!providerHasInitiated) {
      /* ignoring the first render as the `imageAttrState` value provided from the Provider
       * has not been set yet, and instead defaults to empty initial values. Without this skip,
       * the `setImageState` function will accidentaly set the `imageAttrState` to the default values.
       *
       * once the values have been initialized, the `providerHasInitiated` will be updated by the provider,
       * and this function can run as desired.
       */
      return; // ignore the first render
    }

    if (selectedRemoteImageNode || selectedAsset || selectedEditorImageNode) {
      setImageState(selectedRemoteImageNode as HTMLImageElement, selectedAsset as Asset);
    }
  }, [selectedRemoteImageNode, selectedAsset, providerHasInitiated]);

  useEffect(() => {
    if (selectedEditorImageNode) {
      handleNonCustomizableAttrs(selectedEditorImageNode);
    }
  }, [selectedEditorImageNode]);

  const handleResetDimensions = () => {
    const attrValues = {} as ImageAttrState;

    if (selectedRemoteImageNode) {
      attrValues[ImageAttrs.HEIGHT] = selectedRemoteImageNode?.naturalHeight.toString();
      attrValues[ImageAttrs.WIDTH] = selectedRemoteImageNode?.naturalWidth.toString();
    } else if (selectedAsset) {
      attrValues[ImageAttrs.HEIGHT] = selectedAsset?.height.toString();
      attrValues[ImageAttrs.WIDTH] = selectedAsset?.width.toString();
    } else if (selectedEditorImageNode) {
      attrValues[ImageAttrs.HEIGHT] = selectedEditorImageNode?.naturalHeight.toString();
      attrValues[ImageAttrs.WIDTH] = selectedEditorImageNode?.naturalWidth.toString();
    }
    setImageAttrState({ type: ImageAttrBatch.UPDATE_BATCH, value: attrValues });
  };

  return (
    <DetailViewBody>
      <section data-test={testIds.breadcrumb}>
        <Breadcrumb
          isButton
          breadcrumb={{
            content: t('link.creativeLibrary'),
            onClick: () => setPanelView(PanelView.List),
          }}
          variant={PAGE_HEADER_VARIANTS.SECONDARY}
        />
      </section>
      <GridContainer>
        <StyledSection data-test={testIds.preview}>
          {onInsert || onSelect ? (
            <FilePreview $imgSrc={assetUrl}>
              <FilePreviewReplace
                data-test={testIds.previewReplace}
                onDoubleClick={() => setPanelView(PanelView.List)}
              >
                <Button
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.SECONDARY}
                  onClick={() => setPanelView(PanelView.List)}
                >
                  <Refresh />
                  <span>{t('button.changeImage')}</span>
                </Button>
              </FilePreviewReplace>
            </FilePreview>
          ) : (
            <FilePreview $imgSrc={assetUrl} />
          )}
        </StyledSection>
        {!!onInsert && !isBeeEditor ? (
          <StyledSection data-test={testIds.htmlAttrs}>
            <InputImageURL value={assetUrl} />
            <div>
              <AltTextInput
                value={imageAttrState[ImageAttrs.ALT_DESC]}
                onChange={(value: string) =>
                  setImageAttrState({ type: ImageAttrs.ALT_DESC, value })
                }
                showDescription
                dataTest={testIds.inputAltText}
              />
            </div>
            <DetailsDimensions>
              <div>
                <FormField inputId="asset-form-width" labelText={t('input.width.label')}>
                  <InputText
                    data-test={testIds.inputWidth}
                    id="asset-form-width"
                    onChange={(value: string) => handleResponsiveSizing(value, ImageAttrs.WIDTH)}
                    Suffix={<span>{t('abbreviation.pixel')}</span>}
                    type="text"
                    value={imageAttrState[ImageAttrs.WIDTH]}
                  />
                </FormField>
              </div>
              <div>
                <FormField inputId="asset-form-height" labelText={t('input.height.label')}>
                  <InputText
                    data-test={testIds.inputHeight}
                    id="asset-form-height"
                    onChange={(value: string) => handleResponsiveSizing(value, ImageAttrs.HEIGHT)}
                    Suffix={<span>{t('abbreviation.pixel')}</span>}
                    type="text"
                    value={imageAttrState[ImageAttrs.HEIGHT]}
                  />
                </FormField>
              </div>
            </DetailsDimensions>
            <DetailsProportions>
              <CheckboxField>
                <Checkbox
                  data-test={testIds.checkboxAR}
                  id="lockAspectRatio"
                  name="lockAspectRatio"
                  onClick={() => setLockAspectRatio((ar) => !ar)}
                  status={lockAspectRatio ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
                />
                <Label htmlFor="lockAspectRatio" onClick={() => setLockAspectRatio((ar) => !ar)}>
                  {t('label.keepProportions')}
                </Label>
              </CheckboxField>
              <Button
                data-test={testIds.btnResetDimensions}
                onClick={handleResetDimensions}
                size={BUTTON_SIZES.LARGE}
                variant={BUTTON_VARIANTS.GHOST}
              >
                <span>{t('button.reset')}</span>
              </Button>
            </DetailsProportions>

            <FormField
              helpText={t('input.customStyles.helpText')}
              inputId="asset-form-custom-styles"
              labelText={t('input.customStyles.label')}
            >
              <InputText
                data-test={testIds.inputStyles}
                id="asset-form-custom-styles"
                onChange={(value: string) => setImageAttrState({ type: ImageAttrs.STYLES, value })}
                type="text"
                value={imageAttrState[ImageAttrs.STYLES]}
              />
            </FormField>
            {selectedAsset && hasFileDetails && (
              <FileDetails
                showName
                asset={selectedAsset}
                data-test={selectedAsset ? testIds.detailsSelected : testIds.detailsNode}
              />
            )}
          </StyledSection>
        ) : (
          <StyledSection>
            <InputImageURL value={assetUrl} />

            {selectedAsset && hasFileDetails && (
              <FileDetails
                showName
                asset={selectedAsset}
                data-test={selectedAsset ? testIds.detailsSelected : testIds.detailsNode}
              />
            )}
          </StyledSection>
        )}
      </GridContainer>
    </DetailViewBody>
  );
};
